import {isPassportIdentity} from '@sollapay/domain';
import {findDuplicateSellerIdentityIndexes} from '@sollapay/domain/rules';
import {
  SellerType,
  IdentityDocumentType,
  PartyRole,
  PepPublicRole,
  PepRelationship,
  RelationshipType,
  TaxResidency
} from '@sollapay/enums';
import {resolveFullNameValidationError} from '@sollapay/utils';
import {z} from 'zod';

import {YEAR_MONTH_RE} from '@/components/pep';
import {
  addFutureDateIssue,
  addIndividualIdentityIssues,
  FULL_NAME_ERROR_KEYS
} from '@/lib/validation';

const pepPartySchema = z.object({
  relationship: z.union([z.nativeEnum(PepRelationship), z.literal('')]),
  fullName: z.string(),
  countryOfOffice: z.string(),
  publicRole: z.union([z.nativeEnum(PepPublicRole), z.literal('')]),
  stillInRole: z.boolean(),
  roleStartMonth: z.string(),
  roleEndMonth: z.string(),
  sourceOfWealth: z.string()
});

const pepSchema = z.object({
  isPep: z.boolean(),
  parties: z.array(pepPartySchema).default([])
});

const SellerEntryObjectSchema = z.object({
  id: z.string().optional(),
  sellerRole: z.union([z.nativeEnum(PartyRole), z.literal('')]),
  sellerType: z
    .union([z.nativeEnum(SellerType), z.literal('')])
    .refine(v => v !== '', {message: 'common:required'}),
  relationshipType: z.union([z.nativeEnum(RelationshipType), z.literal('')]),
  metFaceToFace: z.enum(['yes', 'no', '']),
  fullName: z.string().min(1, 'common:required'),
  idNumber: z.string().min(1, 'common:required'),
  idType: z.nativeEnum(IdentityDocumentType),
  idCountry: z.string(),
  birthOrIncorporationDate: z.string().min(1, 'common:required'),
  address: z.string().min(1, 'common:required'),
  taxResidency: z
    .union([z.nativeEnum(TaxResidency), z.literal('')])
    .refine(v => v !== '', {message: 'common:required'}),
  taxResidencyCountry: z.string(),
  pep: pepSchema
});

export interface SellerSchemaOptions {
  asksRelationshipQuestions: boolean;
  asksSellerRole: boolean;
}

/**
 * Both flags mirror their `TrustTypeStrategy` counterparts, so a field is required here exactly
 * when the server's readiness check requires it — general sale never asks
 * `relationshipType`/`metFaceToFace`, and only an option assignment asks `sellerRole`.
 * Same pattern as `createTrustScopeFormSchema`'s `requiresTransactionType`.
 */
export function createSellerEntrySchema({
  asksRelationshipQuestions,
  asksSellerRole
}: SellerSchemaOptions) {
  return SellerEntryObjectSchema.superRefine((data, ctx) => {
    if (asksSellerRole && !data.sellerRole) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sellerRole'],
        message: 'common:required'
      });
    }
    if (asksRelationshipQuestions && !data.relationshipType) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['relationshipType'],
        message: 'common:required'
      });
    }
    if (asksRelationshipQuestions && !data.metFaceToFace) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['metFaceToFace'],
        message: 'common:required'
      });
    }

    if (data.taxResidency === TaxResidency.OTHER && !data.taxResidencyCountry.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['taxResidencyCountry'],
        message: 'common:required'
      });
    }

    addFutureDateIssue(ctx, 'birthOrIncorporationDate', data.birthOrIncorporationDate);

    const isCorporate = data.sellerType === SellerType.CORPORATION;
    const isPassport = !isCorporate && isPassportIdentity(data.idType);

    // Skip only when truly empty — `.min(1)` accepts whitespace, so that must still be checked.
    if (data.fullName) {
      const error = resolveFullNameValidationError({isCorporate, value: data.fullName});
      if (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['fullName'],
          message: FULL_NAME_ERROR_KEYS[error]
        });
      }
    }

    addIndividualIdentityIssues(ctx, {
      isCorporate,
      isPassport,
      idNumber: data.idNumber,
      idCountry: data.idCountry,
      idNumberPath: 'idNumber'
    });

    const {isPep, parties} = data.pep;

    if (!isPep) return;

    if (parties.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pep', 'parties'],
        message: 'common:required'
      });
      return;
    }

    parties.forEach((party, i) => {
      if (isCorporate && !party.relationship) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pep', 'parties', i, 'relationship'],
          message: 'common:required'
        });
      }
      if (isCorporate && !party.fullName.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pep', 'parties', i, 'fullName'],
          message: 'common:required'
        });
      }
      if (!party.countryOfOffice.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pep', 'parties', i, 'countryOfOffice'],
          message: 'common:required'
        });
      }
      if (!party.publicRole) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pep', 'parties', i, 'publicRole'],
          message: 'common:required'
        });
      }
      if (!party.sourceOfWealth.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pep', 'parties', i, 'sourceOfWealth'],
          message: 'common:required'
        });
      }
      if (!YEAR_MONTH_RE.test(party.roleStartMonth)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pep', 'parties', i, 'roleStartMonth'],
          message: 'common:required'
        });
      }
      if (!party.stillInRole && !YEAR_MONTH_RE.test(party.roleEndMonth)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pep', 'parties', i, 'roleEndMonth'],
          message: 'common:required'
        });
      }
      if (
        !party.stillInRole &&
        YEAR_MONTH_RE.test(party.roleStartMonth) &&
        YEAR_MONTH_RE.test(party.roleEndMonth) &&
        party.roleEndMonth < party.roleStartMonth
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pep', 'parties', i, 'roleEndMonth'],
          message: 'activation.packages.trustDetails.form.errors.roleEndMonthBeforeStart'
        });
      }
    });
  });
}

/** Per-seller rules match the server; the non-empty roster is a form-readiness rule, since the API accepts an empty draft. */
export function createSellersFormSchema(options: SellerSchemaOptions) {
  const sellersSchema = z
    .array(createSellerEntrySchema(options))
    .min(1, 'activation.packages.trustDetails.form.errors.sellersRequired');

  return z.object({sellers: sellersSchema}).superRefine((data, ctx) => {
    const identities = data.sellers.map(seller => ({
      sellerType: seller.sellerType || null,
      idNumber: seller.idNumber,
      idType: seller.idType,
      idCountry: seller.idCountry
    }));

    for (const index of findDuplicateSellerIdentityIndexes(identities)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sellers', index, 'idNumber'],
        message: 'activation.packages.trustDetails.form.errors.duplicateSellerIdentifier'
      });
    }
  });
}

export type SellerEntryFormValues = z.infer<typeof SellerEntryObjectSchema>;
